import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import { all_routes } from "../router/all_routes";
import Table from "../../core/common/dataTable/index";
import GoalTypeModal from "../../core/modals/goalTypeModal";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch } from "../../core/data/redux/store";
import {
  createGoaltype,
  fetchAllGoaltype,
  deleteGoaltype,
  UpdateGoaltype,
} from "../../core/data/redux/performance/goaltype/goaltypeAction";
import DeleteModal1 from "../../core/modals/deleteModal1";
import Select from "react-select";
import { fetchAllIndicator } from "../../core/data/redux/performance/indicator/indicatorAction";
import {
  createKRA,
  fetchAllKRA,
  updateKRA,
  deleteKRA,
} from "../../core/data/redux/performance/KRA/kraAction";
import { Drawer, message } from "antd";
import { filterData } from "../../core/common/GlobalFunctions/tableFilters";
import { decryptData } from "../../core/utils/encryptUtils";

const Rating = () => {
  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const dispatch = useDispatch<AppDispatch>();
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [Editing, setIsEditing] = useState<any>(false);
  const [selectedSort, setSelectedSort] = useState<any>("Recently Added");
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const { kra, loadingStates } = useSelector((state: any) => state.performance.kra);
  const isLoading = Editing === "edit" ? loadingStates.update : loadingStates.create;
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  const { indicator } = useSelector((state: any) => state.performance.indicator);
  const [selectedRowData, setSelectedRowData] = useState<any>();
  const KRAId = selectedRowData?.id;
  const { user } = useSelector((state: any) => state.auth);
  const [ratings, setRatings] = useState<{ [key: number]: number }>({});
  const [tableKey, setTableKey] = useState(0);
  const [errors, setErrors] = useState<any>({});
  const [formData, setFormData] = useState<any>({
    name: "",
    description: "",
    status: statusOptions[0],
    indicators: [],
    createdBy: user.UserId,
  });

  const formattedIndicator =
    indicator?.map((emp: any) => ({
      value: emp.Id,
      label: emp.Name,
    })) || [];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev: any) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleDelete = (KRAId: any) => {
    dispatch(deleteKRA(KRAId))
      .then((resp: any) => {
        if (resp.meta.requestStatus === "fulfilled") {
          setIsDeleteModalVisible(false);
          message.success(resp.payload.message);
        } else {
          setIsDeleteModalVisible(false);
          message.warning(resp.payload.message);
        }
      })
      .catch((error: any) => {
        console.error("error is ", error);
      });
  };

  const handleEdit = (record: any): void => {
    setSelectedRowData(record);

    setFormData({
      name: record.Name,
      description: record.Description,
      status:
        statusOptions.find((opt: any) => opt.value === record.Status) ||
        statusOptions[0],
      Id: record.Id,
      indicators: Array.isArray(record.indicators)
        ? record.indicators.map((item: any) => ({
          value: item.id,
          label: item.name,
        }))
        : [],
    });

    setIsEditing(true);
    setErrors({});
  };

  const validateForm = () => {
    let formIsValid = true;

    let newErrors: any = {
      name: "",
      description: "",
      status: "",
      indicators: "",
    };

    if (!formData.name.trim()) {
      formIsValid = false;
      newErrors.name = "Name is required.";
    }


    if (!formData.status || formData.status === "Select") {
      formIsValid = false;
      newErrors.status = "Status is required.";
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const sanitizedData = {
      ...formData,
      indicators: formData.indicators.map(({ rating, ...rest }: any) => rest),
      status: formData.status.value,
      createdBy: user.UserId,
    };

    if (Editing) {
      dispatch(updateKRA({ KRAId, data: sanitizedData })).then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          message.success(res.payload.message);
          dispatch(fetchAllKRA());
          setIsDrawerVisible(false)
        } else {
          message.warning(res.payload.message);
        }
      });
    } else {
      dispatch(createKRA(sanitizedData)).then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          message.success(res.payload.message);
          setFormData({
            name: "",
            description: "",
            status: statusOptions[0],
            indicators: [],
            createdBy: user.UserId,
          });
          setIsDrawerVisible(false)
          dispatch(fetchAllKRA());
        } else {
          message.warning(res.payload.message);
        }
      });
    }
  };

  useEffect(() => {
    dispatch(fetchAllIndicator());
    dispatch(fetchAllKRA());
  }, []);

  const routes = all_routes;
  const data = kra ? kra : [];

  const filteredData = useMemo(() => {
    return filterData(
      data || [],
      { status: selectedStatus },
      "created_at",
      "created_at",
      selectedSort
    );
  }, [dispatch, selectedStatus, kra, selectedSort]);

  useEffect(() => {
    setTableKey((prevKey) => prevKey + 1);
  }, [dispatch, kra, selectedStatus, selectedSort]);


  const encryptedPermissions = localStorage.getItem('currentPermissions');
  const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];

  const canAdd =
    permissions.includes("Add") || permissions.includes("AllowAll");
  const canView =
    permissions.includes("View") || permissions.includes("AllowAll");
  const canEdit =
    permissions.includes("Edit") || permissions.includes("AllowAll");
  const canDelete =
    permissions.includes("Delete") || permissions.includes("AllowAll");

  const columns = [
    {
      title: "Name",
      dataIndex: "Name",
      sorter: (a: any, b: any) => a.Name.length - b.Name.length,
    },
    {
      title: "Description",
      dataIndex: "Description",
      sorter: (a: any, b: any) => a.Description.length - b.Description.length,
    },
    {
      title: "Status",
      dataIndex: "Status",
      render: (text: string) => {
        const badgeClass =
          text.toLowerCase() === "active"
            ? "badge-success"
            : text.toLowerCase() === "inactive"
              ? "badge-danger"
              : "badge-secondary";

        return (
          <span
            className={`badge ${badgeClass} d-inline-flex align-items-center badge-xs`}
          >
            <i className="ti ti-point-filled me-1" />
            {text ? text.charAt(0).toUpperCase() + text.slice(1) : ""}

          </span>
        );
      },
      sorter: (a: any, b: any) => a.Status.length - b.Status.length,
    },

    {
      title: "Action",
      dataIndex: "actions",
      render: (text: string, record: any) => (
        <div className="action-icon d-inline-flex gap-2">
          {/* Edit: Always enabled */}
          <button
            onClick={() => { if (!canEdit) return; setIsEditing(true); handleEdit(record); setIsDrawerVisible(true); }}
            disabled={!canEdit}
            style={{ background: "none", border: "none", padding: 0, cursor: canEdit ? "pointer" : "not-allowed", opacity: canEdit ? 1 : 0.5, }}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title={!canEdit ? "Permission Denied" : "Edit"}
          >
            <i className="ti ti-edit" />
          </button>

          {/* Delete: Disabled when status is "inactive" */}
          <button
            onClick={() => {if (!canDelete || record.Status === "inactive") return;setSelectedRowData(record);setIsDeleteModalVisible(true);}}
            disabled={!canDelete || record.Status === "inactive"}
            style={{background: "none",border: "none",padding: 0,cursor: !canDelete || record.Status === "inactive" ? "not-allowed" : "pointer",opacity: !canDelete || record.Status === "inactive" ? 0.5 : 1,}}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title={!canDelete ? "Permission Denied" : record.Status === "inactive" ? "Item is inactive" : "Delete"}
          >
            <i className="ti ti-trash" />
          </button>
        </div>
      ),
    },
  ];
  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">KRA</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Performance</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Add New KRA{" "}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              {canAdd && (
                <div className="mb-2">
                  <Link
                    to="#"
                    className="btn btn-primary d-flex align-items-center"
                    onClick={() => {
                      setIsEditing(false);
                      setIsDrawerVisible(true);
                      setErrors({});
                    }}
                  >
                    <i className="ti ti-circle-plus me-2" />
                    Add KRA{" "}
                  </Link>
                </div>
              )}
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          {/* Performance Indicator list */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>KRA List</h5>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-3">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {selectedStatus
                      ? selectedStatus.charAt(0).toUpperCase() +
                      selectedStatus.slice(1)
                      : "Select Status"}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("active")}
                        className="dropdown-item rounded-1"
                      >
                        Active
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("inactive")}
                        className="dropdown-item rounded-1"
                      >
                        Inactive
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    Sort By : Last 7 Days
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Recently Added
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Ascending
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Desending
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              <Table
                key={tableKey}
                dataSource={filteredData}
                columns={columns}
                Selection={true}
              />
            </div>
          </div>
          {/* /Performance Indicator list */}
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© TalentFlow.</p>
          <p>
            Designed &amp; Developed By{""}
            <Link to="#" className="text-primary">
              Leadchain Systems
            </Link>
          </p>
        </div>
      </div>

      <Drawer
        title={`${Editing ? "Edit" : "Add"} KRA`}
        placement="right"
        onClose={() => setIsDrawerVisible(false)}
        open={isDrawerVisible}
        width={480}
        style={{ marginTop: 50 }}
      >
        <form onSubmit={handleSubmit}>
          <div className="modal-body pb-0">
            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Name <span className="text-danger"> *</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    value={formData.name}
                    onChange={handleChange}
                  />
                  {errors.name && (
                    <div style={{ color: "red" }}>{errors.name}</div>
                  )}
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Status <span className="text-danger"> *</span>
                  </label>
                  <Select
                    className="select"
                    options={statusOptions}
                    value={formData.status}
                    onChange={(selectedOption: any) => {
                      console.log(selectedOption?.value);
                      setFormData((prev: any) => ({
                        ...prev,
                        status: selectedOption,
                      }));
                    }}
                  />
                  {errors.status && (
                    <div style={{ color: "red" }}>{errors.status}</div>
                  )}
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Description
                  </label>
                  <textarea
                    name="description"
                    className="form-control"
                    value={formData.description}
                    onChange={handleChange}
                  />
                  {errors.description && (
                    <div style={{ color: "red" }}>{errors.description}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-light me-2"
              onClick={() => setIsDrawerVisible(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading && <span className="spinner-border spinner-border-sm me-2" />}
              {Editing ? "Edit" : "Add"} KRA
            </button>
          </div>
        </form>
      </Drawer>

      <DeleteModal1
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={() =>
          selectedRowData?.id && handleDelete(selectedRowData.id)
        }
      />
    </>
  );
};

export default Rating;
